// Admin service for fetching users from Cognito

export interface CognitoUser {
  username: string;
  email: string;
  userId: string;
  status: string;
  enabled: boolean;
  createdAt: Date;
  groups: string[];
}

// Cognito User Pool ID from amplifyconfiguration
const USER_POOL_ID = 'ca-central-1_RhLotO9je';
const REGION = 'ca-central-1';

export const listCognitoUsers = async (): Promise<CognitoUser[]> => {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const credentials = session.credentials;

    if (!credentials) {
      throw new Error('No credentials available');
    }

    // Use AWS SDK to list users
    const { CognitoIdentityProviderClient, ListUsersCommand, AdminListGroupsForUserCommand } =
      await import('@aws-sdk/client-cognito-identity-provider');

    const client = new CognitoIdentityProviderClient({
      region: REGION,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
    });

    // List all users
    const listUsersCommand = new ListUsersCommand({
      UserPoolId: USER_POOL_ID,
    });

    const usersResponse = await client.send(listUsersCommand);
    const users: CognitoUser[] = [];

    for (const user of usersResponse.Users || []) {
      // Get user's groups
      let groups: string[] = [];
      try {
        const groupsCommand = new AdminListGroupsForUserCommand({
          UserPoolId: USER_POOL_ID,
          Username: user.Username || '',
        });
        const groupsResponse = await client.send(groupsCommand);
        groups = (groupsResponse.Groups || []).map(g => g.GroupName || '');
      } catch (err) {
        console.error('Error fetching groups for user:', user.Username, err);
      }

      const emailAttr = user.Attributes?.find(a => a.Name === 'email');
      const subAttr = user.Attributes?.find(a => a.Name === 'sub');

      users.push({
        username: user.Username || '',
        email: emailAttr?.Value || '',
        userId: subAttr?.Value || user.Username || '',
        status: user.UserStatus || 'UNKNOWN',
        enabled: user.Enabled || false,
        createdAt: user.UserCreateDate || new Date(),
        groups,
      });
    }

    return users;
  } catch (error) {
    console.error('Error listing Cognito users:', error);
    throw error;
  }
};
